import { useSearchParams } from 'react-router';

/**
 * Which tab of a tabbed page is open, kept in the address rather than in component state.
 *
 * Two things break without it, and both are the same bug wearing different clothes: a page can
 * only ever be linked to at its FIRST tab. "Commission owed · L 2,000" on the dashboard led to
 * the partners list rather than to the money; a record screen's back link promising "All roles"
 * landed on the list of people; and a browser refresh threw away whichever tab the reader was
 * working in.
 *
 * The value is validated against the tabs the page actually has, so a stale or hand-typed address
 * opens the default rather than a blank panel — Mantine renders nothing at all for a `value` that
 * matches no panel, which reads as the page having lost its content.
 *
 * `replace` on the way out: switching tabs is not somewhere to go back to, and stacking one history
 * entry per tab makes the browser's back button walk sideways through a page instead of leaving it.
 *
 * This hook owns exactly one key — `tab` — and must leave the rest of the address alone. Writing a
 * whole new query string here silently deletes state that belongs to the page: on Prices, which
 * also keeps WHICH LIST is open in the address, opening "Prices on Base Menu", glancing at another
 * tab and coming back left the heading reading "Prices on Happy Hour", because the list had been
 * dropped and the panel fell back to the first one. A price typed after that lands on the wrong
 * list, and two prices for one drink on one list is the hazard the pricing screens are built to
 * avoid.
 */
export function useTabInUrl<T extends string>(
  tabs: readonly T[],
  fallback: T,
): [T, (next: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const asked = params.get('tab');
  const current = tabs.find((t) => t === asked) ?? fallback;

  const setTab = (next: string | null) => {
    const chosen = tabs.find((t) => t === next) ?? fallback;
    setParams(
      (previous) => {
        const merged = new URLSearchParams(previous);
        // The default tab is the address with no `tab` on it at all, so the plain page URL and the
        // URL for its first tab are one address rather than two that render identically.
        if (chosen === fallback) merged.delete('tab');
        else merged.set('tab', chosen);
        return merged;
      },
      { replace: true },
    );
  };

  return [current, setTab];
}
